import type { ITeamCycleTimeDataPoint } from '@skystone/shared';
import { computeP85 } from '@/lib/metric-view/statistics';

interface ICycleTimeScatterSummaryProps {
  points: ITeamCycleTimeDataPoint[];
  /** Label describing which filter narrow produced `points` — e.g. "items", "Bugs", "Stories" */
  itemLabel: string;
}

/**
 * Narrow-width fallback for the Cycle Time scatter. Shows a single summary
 * line instead of plotting every dot — the scatter isn't legible below
 * ~400px card width. Parent component filters `points` by the active type
 * before passing them in.
 */
export function CycleTimeScatterSummary({ points, itemLabel }: Readonly<ICycleTimeScatterSummaryProps>) {
  if (points.length === 0) {
    return (
      <div className="flex h-[120px] items-center justify-center">
        <p className="text-center text-sm text-muted-foreground">
          No closed issues with cycle time in this period.
        </p>
      </div>
    );
  }

  const values = points.map((p) => p.cycleTimeDays);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const p85 = computeP85(values);

  return (
    <div className="flex h-[120px] flex-col items-center justify-center gap-1 text-sm">
      <p className="text-foreground">
        <span className="font-semibold tabular-nums">{points.length}</span>{' '}
        <span className="text-muted-foreground">{itemLabel}</span>
      </p>
      <p className="text-xs text-muted-foreground">
        P85{' '}
        <span className="font-medium text-foreground tabular-nums">
          {p85 === null ? '—' : `${Math.round(p85)}d`}
        </span>
        {' · Range '}
        <span className="font-medium text-foreground tabular-nums">
          {low}–{high}d
        </span>
      </p>
    </div>
  );
}
